import * as React from "react"

const MOBILE_BREAKPOINT = 768
const FORCE_DESKTOP_KEY = "forceDesktop"

export function useIsMobile() {
  // Initialize with a proper default based on initial check to avoid undefined state
  const getInitialMobileState = () => {
    // Check localStorage override first
    const forceDesktop = localStorage.getItem(FORCE_DESKTOP_KEY) === "true"
    if (forceDesktop) return false
    
    // Check for iPad/tablet devices - always desktop
    const userAgent = navigator.userAgent
    // iPad detection: explicit iPad OR Macintosh with touch support (iPadOS reports as Mac)
    const isIPad = /iPad/i.test(userAgent) || 
                   (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)
    const isAndroidTablet = /Android.*Tablet|Tablet/i.test(userAgent)
    const isTablet = isIPad || isAndroidTablet
    if (isTablet) return false
    
    // Width-based detection is primary
    const width = window.innerWidth
    const isMobileWidth = width < MOBILE_BREAKPOINT
    
    // If width is >= 768px (tablet/desktop), always treat as desktop
    if (!isMobileWidth) return false
    
    // For widths < 768px, check user agent for small phones
    const isMobileUA = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(userAgent)
    
    // Calculate final mobile detection - only true if width < 768px AND not a tablet
    return isMobileWidth
  }
  
  const [isMobile, setIsMobile] = React.useState<boolean>(getInitialMobileState())

  React.useEffect(() => {
    const checkMobile = () => {
      // Check localStorage override first
      const forceDesktop = localStorage.getItem(FORCE_DESKTOP_KEY) === "true"
      
      // Width-based detection is primary
      const width = window.innerWidth
      const userAgent = navigator.userAgent
      const isMobileWidth = width < MOBILE_BREAKPOINT
      
      // Check for iPad/tablet devices - always desktop
      // iPad detection: explicit iPad OR Macintosh with touch support (iPadOS reports as Mac)
      const isIPad = /iPad/i.test(userAgent) || 
                     (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)
      const isAndroidTablet = /Android.*Tablet|Tablet/i.test(userAgent)
      const isTablet = isIPad || isAndroidTablet
      
      // Debug logging
      console.log('📱 [MOBILE HOOK] Detection Details:', {
        width,
        isMobileWidth,
        isIPad,
        isAndroidTablet,
        isTablet,
        maxTouchPoints: navigator.maxTouchPoints,
        forceDesktop,
        finalResult: forceDesktop ? false : (isTablet ? false : isMobileWidth),
        userAgent: userAgent.substring(0, 80),
        currentPath: window.location.pathname
      });
      
      if (forceDesktop) {
        console.log('🔒 [MOBILE HOOK] Desktop mode forced via localStorage');
        return false
      }
      
      // Tablets always get desktop view regardless of width
      if (isTablet) {
        console.log('📱 [MOBILE HOOK] Tablet detected - forcing desktop view');
        return false
      }
      
      // Tablets (768px+) and desktops always get desktop view
      // Only widths < 768px get mobile view
      return isMobileWidth
    }

    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(checkMobile())
    }
    
    // Listen for localStorage changes (for same-tab updates)
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === FORCE_DESKTOP_KEY) {
        setIsMobile(checkMobile())
      }
    }
    
    mql.addEventListener("change", onChange)
    window.addEventListener("storage", onStorageChange)
    setIsMobile(checkMobile())
    
    return () => {
      mql.removeEventListener("change", onChange)
      window.removeEventListener("storage", onStorageChange)
    }
  }, [])

  return isMobile
}

// Utility functions for managing desktop override
export function setForceDesktop(force: boolean) {
  if (force) {
    localStorage.setItem(FORCE_DESKTOP_KEY, "true")
  } else {
    localStorage.removeItem(FORCE_DESKTOP_KEY)
  }
  // Trigger storage event for same-tab updates
  window.dispatchEvent(new StorageEvent('storage', {
    key: FORCE_DESKTOP_KEY,
    newValue: force ? "true" : null,
    oldValue: localStorage.getItem(FORCE_DESKTOP_KEY)
  }))
}

export function getForceDesktop(): boolean {
  return localStorage.getItem(FORCE_DESKTOP_KEY) === "true"
}
